import * as React from 'react';
import { connect } from 'react-redux';
import {
  NATIVE_TGB_BANNER_SIZE,
  NATIVE_TGB_POSITIONS,
  PARALLAX_BANNER_SIZE,
  EAdFoxBannerType,
} from '../../constants/adfox';
import { selectAdditionalAdfoxPuids } from '../../selectors/adfox';
import { TPuidMap } from '../../types/adfox';

import { INewbuilding } from '../../types/newbuilding';
import { Container } from '../../components/Container';
import { GKCard } from './GKCard';
import { BannerContainer } from './BannerContainer';
import { trackingActionToCardNewbuilding } from '../../actions/analytics';
import { IApplicationState } from '../../types/redux';
import { AdfoxContainer } from '../AdfoxContainer';
import { compose } from 'ramda';
import { IVisitedOffersContext, withVisitedOffersContext } from '@cian/frontend-visited-offers-widget';

interface IOffersBodyOwnProps {
  newbuildings: INewbuilding[];
  isPromoNewbuildingDisplayed: boolean;
  promoNewbuilding?: INewbuilding;
  aggregateRating?: number | null;
  reviewsCount?: number | null;
}

interface IOffersBodyState {
  adError: boolean;
}

interface IOffersBodyStateProps {
  page: number;
  additionalPuids: TPuidMap;
}

interface IOffersBodyDispatchProps {
  trackingToCardNewbuilding(): void;
}

type TOffersBodyProps = IOffersBodyOwnProps & IOffersBodyStateProps & IOffersBodyDispatchProps & IVisitedOffersContext;

class OffersBody extends React.PureComponent<TOffersBodyProps, IOffersBodyState> {
  public state: Readonly<IOffersBodyState> = {
    adError: false,
  };

  public render() {
    const { newbuildings, page, aggregateRating, reviewsCount } = this.props;

    return (
      <Container
        dataMark="OffersBody"
        dataId="offers_body_container"
        borderStyle="solid"
        borderColor="gray_1"
        borderTopWidth={1}
        borderLeftWidth={0}
        borderRightWidth={0}
        borderBottomWidth={0}
      >
        {Boolean(aggregateRating) && <meta itemProp="aggregateRating" content={`${aggregateRating}`} />}
        {Boolean(reviewsCount) && <meta itemProp="reviewsCount" content={`${reviewsCount}`} />}
        {newbuildings.map((newbuilding, index) => (
          <React.Fragment key={newbuilding.id}>
            {index === 3 && page === 1 && <BannerContainer />}
            {NATIVE_TGB_POSITIONS.includes(index) && (
              <AdfoxContainer
                postViewAnalyticsData={{
                  banner: {
                    place: 'native_tgb',
                    name: 'Listing_native_TGB',
                    position: index,
                    extra: {
                      offerType: 'TGB_banner',
                    },
                  },
                }}
                type={EAdFoxBannerType.NATIVE}
                size={NATIVE_TGB_BANNER_SIZE}
              />
            )}
            {index === 10 && !this.state.adError && (
              <AdfoxContainer
                onError={this.handleAdError}
                postViewAnalyticsData={{
                  banner: {
                    place: 'newbuildingsSearch',
                    name: 'Listing_parallax',
                    position: index,
                  },
                }}
                type={EAdFoxBannerType.PARALLAX}
                size={PARALLAX_BANNER_SIZE}
              />
            )}

            {!this.isNewbuildingHidden(newbuilding) && (
              <GKCard
                newbuilding={newbuilding}
                isEven={index !== 0 && index % 2 === 0}
                onCardClick={this.handleCardClick}
              />
            )}
          </React.Fragment>
        ))}
      </Container>
    );
  }

  private handleAdError = () => {
    this.setState({
      adError: true,
    });
  };

  private isNewbuildingHidden = (newbuilding: INewbuilding) => {
    const { page, promoNewbuilding, isPromoNewbuildingDisplayed } = this.props;

    return page === 1 && isPromoNewbuildingDisplayed && promoNewbuilding && promoNewbuilding.id === newbuilding.id;
  };

  private handleCardClick = (offerId: number) => {
    const { trackingToCardNewbuilding, setOfferVisited } = this.props;

    trackingToCardNewbuilding();
    setOfferVisited(offerId);
  };
}

const mapStateToProps = (state: IApplicationState): IOffersBodyStateProps => ({
  page: state.location.page,
  additionalPuids: selectAdditionalAdfoxPuids(state),
});

const mapDispatchToProps: IOffersBodyDispatchProps = {
  trackingToCardNewbuilding: trackingActionToCardNewbuilding,
};

export function mapContextToProps({ setOfferVisited }: IVisitedOffersContext) {
  return {
    setOfferVisited,
  };
}

export const OffersBodyContainer = compose(
  withVisitedOffersContext(mapContextToProps),
  connect(mapStateToProps, mapDispatchToProps),
)(OffersBody) as React.ComponentType<IOffersBodyOwnProps>;
